import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Star } from "lucide-react";
import { api } from "@/lib/api";

const FIELDS = [
  { key: "on_time", label: "On-time service", options: ["Yes", "No"] },
  { key: "engineer_behaviour", label: "Engineer behaviour", options: ["Good", "Average", "Poor"] },
  { key: "engineer_explanation", label: "Engineer explanation", options: ["Good", "Average", "Poor"] },
  { key: "warranty_explained", label: "After repair, warranty terms explained", options: ["Yes", "No"] },
  { key: "spare_replaced", label: "Any spare replaced", options: ["Yes", "No"] },
  { key: "overall_experience", label: "Overall experience", options: ["Excellent", "Good", "Average"] },
];

const inputClass =
  "w-full h-14 bg-[#0C1C2E] border border-[#223851] rounded-xl px-4 text-white placeholder:text-[#6B7E92] focus:border-[#F26A21] focus:outline-none focus:ring-2 focus:ring-[#F26A21]/30 transition-all";

function Segmented({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const active = value === o;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            data-testid={`${testid}-${o.toLowerCase()}`}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors duration-200 ${
              active
                ? "bg-[#F26A21] border-[#F26A21] text-white"
                : "bg-white/5 border-[#33506e] text-[#AEBECD] hover:border-[#F26A21] hover:text-white"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

export default function Feedback() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    customer_id: params.get("id") || "",
    phone: "",
    on_time: "",
    engineer_behaviour: "",
    engineer_explanation: "",
    warranty_explained: "",
    spare_replaced: "",
    overall_experience: "",
    remarks: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    const missing = FIELDS.find((f) => !form[f.key]);
    if (missing) {
      toast.error(`Please rate: ${missing.label}`);
      return;
    }
    setLoading(true);
    try {
      await api.post("/feedback", form);
      setDone(true);
      toast.success("Thank you! Your feedback has been recorded.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="feedback-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(242,106,33,0.10),transparent_55%)]" />
        <div className="relative max-w-[1000px] mx-auto px-5 sm:px-8 pt-14 md:pt-20 pb-6">
          <motion.p className="overline text-[#F26A21] mb-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            Customer feedback
          </motion.p>
          <motion.h1
            className="font-heading font-bold text-5xl sm:text-6xl tracking-tight text-white leading-[1.05]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            How did we{" "}
            <span className="text-[#F26A21]">do?</span>
          </motion.h1>
          <p className="text-[#AEBECD] text-lg max-w-xl mt-5 leading-relaxed">
            Your feedback helps us keep our engineers sharp and our service
            honest. It takes less than a minute.
          </p>
        </div>
      </section>

      <section className="max-w-[1000px] mx-auto px-5 sm:px-8 py-12">
        {done ? (
          <motion.div
            data-testid="feedback-success"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl bg-[#122539] border border-[#223851] p-12 text-center"
          >
            <span className="w-16 h-16 rounded-full bg-[#F26A21]/15 flex items-center justify-center mx-auto">
              <Star size={30} className="text-[#F26A21]" />
            </span>
            <h2 className="font-heading font-bold text-3xl text-white mt-6">Thank you!</h2>
            <p className="text-[#AEBECD] mt-3 max-w-md mx-auto">
              We've recorded your feedback. Every response helps us serve your
              home better.
            </p>
          </motion.div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="feedback-form"
            className="rounded-3xl bg-[#122539] border border-[#223851] p-6 sm:p-10 space-y-8"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="overline text-[#8CA0B4] block mb-2">Your name *</label>
                <input data-testid="feedback-name" className={inputClass} value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Full name" />
              </div>
              <div>
                <label className="overline text-[#8CA0B4] block mb-2">Email (for confirmation)</label>
                <input data-testid="feedback-email" type="email" className={inputClass} value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="you@email.com" />
              </div>
              <div>
                <label className="overline text-[#8CA0B4] block mb-2">Customer ID</label>
                <input data-testid="feedback-customer-id" className={inputClass} value={form.customer_id} onChange={(e) => setField("customer_id", e.target.value.toUpperCase())} placeholder="ASA-123456" />
              </div>
              <div>
                <label className="overline text-[#8CA0B4] block mb-2">Phone</label>
                <input data-testid="feedback-phone" className={inputClass} value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="+91 ..." />
              </div>
            </div>

            <div className="space-y-6 pt-2">
              {FIELDS.map((f) => (
                <div key={f.key} className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-[#223851] pb-6 last:border-b-0">
                  <p className="text-white font-medium">{f.label}</p>
                  <Segmented
                    options={f.options}
                    value={form[f.key]}
                    onChange={(v) => setField(f.key, v)}
                    testid={`feedback-${f.key}`}
                  />
                </div>
              ))}
            </div>

            <div>
              <label className="overline text-[#8CA0B4] block mb-3">Remarks & feedback</label>
              <textarea
                data-testid="feedback-remarks"
                className="w-full bg-[#0C1C2E] border border-[#223851] rounded-xl px-4 py-3 text-white placeholder:text-[#6B7E92] focus:border-[#F26A21] focus:outline-none focus:ring-2 focus:ring-[#F26A21]/30 transition-all min-h-[120px] resize-none"
                value={form.remarks}
                onChange={(e) => setField("remarks", e.target.value)}
                placeholder="Tell us anything else about your experience..."
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="feedback-submit"
              className="w-full h-14 bg-[#F26A21] text-white rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-[#DE5C15] transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Submit feedback"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
